import { useCallback, useMemo } from 'react';

import { ChannelIcon } from 'src/components/ChannelIcon';
import { doesAssetSupportPublishing } from 'src/data/asset';
import { CHANNEL, getChannelLabel } from 'src/data/channel';
import { doesToolSupportCsvExport, GENERATION_TOOL } from 'src/data/generationTool';
import { useFreshRef } from 'src/lib/hooks';
import { captureMessage } from 'src/lib/sentry';
import { useChannelAccounts, useConnectToChannel } from 'src/services';
import { useCustomerPublishIntegrationsEnabled } from 'src/services/customer';
import { useSprinklrIntegrationEnabled } from 'src/services/sprinklr';
import { useNotificationActions } from 'src/store';

import { useExportAssetNamePopup } from '../components/ExportAssetNamePopup';
import { useListViewActions } from '../store';
import { exportGoogleAdsHandler } from '../utils/';

import { useListViewEvents } from './useListViewEvents';

const BASE_CHANNELS = [CHANNEL.salesforceExport, CHANNEL.csvExport];
const MULTIPLE_TYPES_DISABLED =
  'This action is disabled because it contains too many assets. Try exporting assets individually.';

export const useExportMenuItems = ({ generationTool, variations }) => {
  const publishIntegrationsEnabled = useCustomerPublishIntegrationsEnabled();
  const sprinklrIntegrationEnabled = useSprinklrIntegrationEnabled();
  const availableChannels = useMemo(
    () => (sprinklrIntegrationEnabled ? [...BASE_CHANNELS, CHANNEL.sprinklrExport] : BASE_CHANNELS),
    [sprinklrIntegrationEnabled]
  );
  const { connectedAccounts } = useChannelAccounts();
  const variationsRef = useFreshRef(variations);
  const handlePublishExport = useExportHandler();

  const variationsPublishIntegrationSupported = useMemo(
    () => checkVariationsAssetsSupported(variations),
    [variations]
  );

  const variationsCsvExportSupported = useMemo(
    () => checkVariationsCsvExportSupported(variations),
    [variations]
  );

  const haveAssetTypeMultiples = useMemo(() => checkAssetTypeMultiples(variations), [variations]);

  const exportAllowed =
    variationsCsvExportSupported ||
    (publishIntegrationsEnabled && variationsPublishIntegrationSupported);

  return useMemo(() => {
    // Do not display any export options if the feature is not enabled for this customer or we have unsupported variations
    if (!exportAllowed) {
      return [];
    }

    return availableChannels
      .map((channel) => {
        // If we have connected accounts, use the first one (for now)
        const channelAccounts = connectedAccounts[channel] || [];
        const account = channelAccounts.length > 0 ? channelAccounts[0] : null;
        const handleExport = channel === CHANNEL.csvExport ? handleCsvExport : handlePublishExport;
        const exportDisabled = haveAssetTypeMultiples && channel !== CHANNEL.csvExport;

        if (channel === CHANNEL.csvExport && !variationsCsvExportSupported) {
          return null; // Skip CSV export if not supported
        }
        if (channel !== CHANNEL.csvExport && !variationsPublishIntegrationSupported) {
          return null; // Skip publish export if not supported
        }

        return {
          id: channel,
          label: getMenuItemLabel({ channel, account }),
          icon: <ChannelIcon channel={channel} />,
          disabled: exportDisabled,
          tooltip: exportDisabled ? MULTIPLE_TYPES_DISABLED : undefined,
          onClick: () =>
            handleExport({
              generationTool,
              channel,
              account,
              variations: variationsRef.current,
            }),
        };
      })
      .filter((item) => !!item);
  }, [
    exportAllowed,
    availableChannels,
    connectedAccounts,
    handlePublishExport,
    generationTool,
    variationsRef,
    haveAssetTypeMultiples,
    variationsCsvExportSupported,
    variationsPublishIntegrationSupported,
  ]);
};

const handleCsvExport = ({ variations }) => {
  switch (variations[0].generationTool) {
    case GENERATION_TOOL.googleRSA:
    case GENERATION_TOOL.googlePerformanceMaxAd:
      exportGoogleAdsHandler(variations);
      break;
    default:
      captureMessage(
        `CSV export error: Unsupported generation tool for CSV export: ${variations[0].generationTool}`,
        {
          level: 'warning',
        }
      );
      return;
  }
};

const useExportHandler = () => {
  const selectChannelAccount = useSelectChannelAccount();
  const { openExportAssetNamePopup } = useExportAssetNamePopup();
  const notifications = useNotificationActions();
  const listViewActions = useListViewActions();
  const { trackInitiatedPublishFlow } = useListViewEvents();

  return useCallback(
    async ({ generationTool, channel, account, variations }) => {
      const variationIds = variations.map((v) => v.id);

      trackInitiatedPublishFlow({
        generationTool,
        channel,
        variationIds,
      });

      // If we don't have account selected, request it from user
      if (!account) {
        account = await selectChannelAccount(channel);
        if (!account) {
          return;
        }
      }

      // Request user to enter "asset name" for the export
      const assetName = await openExportAssetNamePopup(channel);
      if (!assetName) {
        return;
      }

      try {
        // Publish current variation to the selected account
        await listViewActions.publishVariations({
          variationIds,
          dataSource: channel,
          accountId: account.id,
          assetName,
        });

        const message = formatSuccessMessage({ channel });
        notifications.displaySuccess(message);
      } catch (error) {
        notifications.displayError(error);
      }
    },
    [
      trackInitiatedPublishFlow,
      openExportAssetNamePopup,
      selectChannelAccount,
      listViewActions,
      notifications,
    ]
  );
};

const useSelectChannelAccount = () => {
  const { connectToChannel } = useConnectToChannel();

  return useCallback(
    (channel) => {
      return new Promise((resolve) => {
        connectToChannel({
          channel,
          onConnectSuccess: ({ account }) => {
            resolve(account);
          },
        });
      });
    },
    [connectToChannel]
  );
};

const checkVariationsCsvExportSupported = (variations) =>
  variations.every((v) => doesToolSupportCsvExport(v.generationTool));
const checkVariationsAssetsSupported = (variations) =>
  variations.every((v) => doesAssetSupportPublishing(v.assetType));

const formatSuccessMessage = ({ channel }) => {
  switch (channel) {
    case CHANNEL.salesforceExport:
      return 'Copy exported to: “Anyword” folder in your Salesforce Marketing Cloud account.';
    case CHANNEL.sprinklrExport:
      return 'Copy exported to Sprinklr Social Asset Manager as a draft.';
    default:
      return 'Copy exported successfully';
  }
};

const getMenuItemLabel = ({ channel, account }) => {
  if (account) {
    return (
      <span>
        {account.name} <i>({getChannelLabel(channel)})</i>
      </span>
    );
  }

  return <span>{getChannelLabel(channel)}</span>;
};

const checkAssetTypeMultiples = (variations) => {
  const assetTypes = variations.map((v) => v.assetType);
  const uniqueAssetTypes = [...new Set(assetTypes)];
  return assetTypes.length > uniqueAssetTypes.length;
};
